import React, { FC, useState, ChangeEvent } from 'react';
import {
  debounce,
  Typography,
  TextField,
  IconButton,
  Link,
} from '@mui/material';
import { Edit } from '@mui/icons-material';
import { TypographyProps } from '@mui/material/Typography';
import { LinkProps } from '@mui/material/Link';
import { useStyles } from './styles';
import { ErrorTypography } from 'components/ErrorTypography';

interface EditableTypographyProps extends TypographyProps {
  content: string | null | undefined;
  isLink?: boolean;
  multiline?: boolean;
  placeholder?: string;
  onTextFieldChange: (value: string) => void;
  error?: boolean;
  errorMessage?: string;
}

export const EditableTypography: FC<EditableTypographyProps & LinkProps> = ({
  content = '',
  isLink = false,
  multiline = false,
  className,
  variant,
  onTextFieldChange,
  placeholder,
  error,
  errorMessage,
  ...props
}) => {
  const debouncedOnTextFieldChange = debounce(onTextFieldChange, 500);
  const [isInEditMode, setEditMode] = useState<boolean>(false);
  const [fieldValue, setFieldValue] = useState<string>(content || '');
  const classes = useStyles({ variant, isInEditMode });
  const Component: any = isLink ? Link : Typography;
  const finalContent = fieldValue || content || placeholder;

  const handleFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
    const {
      target: { value },
    } = event;
    setFieldValue(value);
    debouncedOnTextFieldChange(value);
  };

  return (
    <div className={className ? className : classes.root}>
      <div className={classes.content}>
        {!isInEditMode && (
          <Component
            variant={variant}
            {...props}
            onClick={() => setEditMode(true)}
          >
            {finalContent}
            {error && (
              <ErrorTypography message={errorMessage || 'Format invalide'} />
            )}
          </Component>
        )}
        {isInEditMode && (
          <TextField
            error={error}
            helperText={errorMessage}
            fullWidth
            autoFocus
            value={fieldValue}
            className={classes.textField}
            multiline={multiline}
            onBlur={() => setEditMode((prevMode) => !prevMode)}
            onChange={handleFieldChange}
            variant="standard"
          />
        )}
        <div className={classes.iconContainer}>
          <IconButton
            size="small"
            className={classes.editIcon}
            onClick={() => setEditMode((prevMode) => !prevMode)}
          >
            <Edit
              classes={{ fontSizeSmall: classes.fontSizeSmall }}
              fontSize="small"
            />
          </IconButton>
        </div>
      </div>
    </div>
  );
};
